import { useState, useRef, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import CityAutocomplete from "@/components/base/CityAutocomplete";
import { INDIAN_STATES } from "@/constants/indianStates";
import { usePageContent } from "@/hooks/usePageContent";
import { useSettings } from "@/context/SettingsContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { buildWhatsAppLink } from "@/lib/utils";
import { isValidPhone } from "@/lib/validation";

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

const API_BASE = import.meta.env.VITE_PUBLIC_API_URL || "";

const trustItems = [
  { icon: "ri-time-line", label: "List in 5 minutes" },
  { icon: "ri-shield-check-line", label: "Verified tenants only" },
  { icon: "ri-money-rupee-circle-line", label: "No upfront cost" },
  { icon: "ri-customer-service-2-line", label: "Dedicated agent support" },
];

const whyListItems = [
  { icon: "ri-group-line", label: "Access to 1,200+ verified tenants" },
  { icon: "ri-shield-check-line", label: "Legal docs handled by experts" },
  { icon: "ri-bar-chart-line", label: "Best-use analysis for maximum ROI" },
  { icon: "ri-timer-line", label: "Most deals close within 3-4 weeks" },
  { icon: "ri-money-rupee-circle-line", label: "No upfront fee - pay only on deal" },
];

const steps = [
  { num: "1", title: "We Review", desc: "Our team reviews your listing within 24 hours" },
  { num: "2", title: "Site Visit", desc: "Agent visits your land for verification & photos" },
  { num: "3", title: "We Market", desc: "Your listing goes live to 1,200+ verified tenants" },
  { num: "4", title: "Deal Closed", desc: "We handle negotiations and paperwork for you" },
];

const categoryOptions = [
  { value: "agricultural", label: "Agricultural" },
  { value: "commercial", label: "Commercial" },
  { value: "industrial", label: "Industrial" },
  { value: "residential", label: "Residential" },
  { value: "mixed_use", label: "Mixed Use" },
];

const transactionOptions = [
  { value: "sale", label: "Sale" },
  { value: "jv", label: "JV" },
  { value: "lease", label: "Lease" },
  { value: "development", label: "Development" },
  { value: "rent", label: "Rent" },
  { value: "any", label: "Any" },
];

export default function SubmitPropertyPage() {
  usePageMeta({
    title: "List Your Land Free — We Find Tenants | CashMyProperty.ai",
    description: "List your land in 5 minutes at zero cost. CashMyProperty.ai verifies, markets and matches your property with 1,200+ vetted tenants — you pay only when a deal closes.",
    canonicalUrl: "https://cashmyproperty.ai/submit-property",
  });
  const navigate = useNavigate();
  const { getContent } = usePageContent('submit-property');
  const heroBadge = stripHtml(getContent('submit-hero-badge')) || 'Start Earning from Your Land';
  const formHeading = stripHtml(getContent('submit-form-heading')) || 'Land Listing Form';
  const formSub = stripHtml(getContent('submit-form-sub')) || 'Fill in your property details and our team will contact you within 24 hours.';
  const whyHeading = stripHtml(getContent('submit-why-heading')) || 'Why List With Us?';
  const stepsHeading = stripHtml(getContent('submit-steps-heading')) || 'What Happens Next?';
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [descCount, setDescCount] = useState(0);
  const [addCount, setAddCount] = useState(0);
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState<string[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { settings } = useSettings();

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const fileArray = Array.from(files);
    const previews = fileArray.map((f) => URL.createObjectURL(f));
    setImageFiles((prev) => [...prev, ...fileArray].slice(0, 5));
    setImagePreviews((prev) => [...prev, ...previews].slice(0, 5));
  };

  const removeImage = (idx: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== idx));
    setImagePreviews((prev) => {
      const url = prev[idx];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const toggleTransactionType = (val: string) => {
    setSelectedTransactionTypes((prev) =>
      prev.includes(val) ? prev.filter((t) => t !== val) : [...prev, val]
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setStatus("idle");
    setErrorMsg("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get("contact_alt") as string;
    if (honeypot && honeypot.trim()) {
      setLoading(false);
      setStatus("success");
      return;
    }

    // Validate mobile number format — catch emails typed into the phone field
    const mobile = (formData.get("mobile") as string) || "";
    if (mobile && !isValidPhone(mobile)) {
      setLoading(false);
      setStatus("error");
      setErrorMsg("Please enter a valid mobile number (7-15 digits, no letters or @).");
      return;
    }

    // Append image files to FormData
    imageFiles.forEach((file) => {
      formData.append("images", file);
    });

    // Append selected transaction types (Available for)
    if (selectedTransactionTypes.length > 0) {
      formData.append("transaction_type", selectedTransactionTypes[0]);
      formData.append("transaction_types", JSON.stringify(selectedTransactionTypes));
    }

    try {
      const res = await fetch(`${API_BASE}/api/public/listings`, {
        method: "POST",
        // Do NOT set Content-Type — browser sets it automatically with boundary for multipart
        body: formData,
      });

      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (!res.ok) {
        const msg = parsed?.error || parsed?.message || responseText || "Submission failed";
        throw new Error(msg);
      }

      setStatus("success");
      form.reset();
      setImageFiles([]);
      setImagePreviews((prev) => {
        prev.forEach((url) => URL.revokeObjectURL(url));
        return [];
      });
      setDescCount(0);
      setAddCount(0);
      setCity('');
      setStateProvince('');
      setSelectedTransactionTypes([]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout title="Submit Property | CashMyProperty.ai">
      {/* Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden flex items-center">
        <div className="absolute inset-0">
          <img
            alt="Landowner surveying land"
            className="w-full h-full object-cover object-top"
            src="https://readdy.ai/api/search-image?query=Indian%20landowner%20standing%20on%20his%20large%20empty%20land%20plot%20surveying%20the%20field%2C%20golden%20hour%20warm%20light%2C%20vast%20open%20land%20with%20clear%20boundaries%2C%20hopeful%20expression%2C%20real%20estate%20photography%20cinematic&width=1800&height=800&seq=submit-hero-001&orientation=landscape&nocache=true"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/80 via-background-950/60 to-background-950" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-primary-500/10 border border-primary-500/20 rounded-full px-4 py-2 mb-6">
            <span className="w-2 h-2 bg-primary-400 rounded-full animate-pulse" />
            <span className="text-primary-400 text-sm font-medium">{heroBadge}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-background-50 mb-5 leading-tight">
            List Your Land.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-primary-500">
              We Find the Tenants.
            </span>
          </h1>
          <p className="text-background-200 text-lg max-w-2xl mx-auto">
            Register your land in under 5 minutes. Our expert agents analyze, market, and close deals — while you sit back and earn.
          </p>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-background-100/50 border-y border-primary-900/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {trustItems.map((item) => (
              <div key={item.label} className="flex items-center justify-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-500/10 rounded-lg shrink-0">
                  <i className={`${item.icon} text-primary-400 text-base`} />
                </div>
                <span className="text-foreground-700 text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Banner */}
      {status === "success" && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6 flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-emerald-500/20 rounded-full shrink-0">
              <i className="ri-check-line text-emerald-500 text-xl" />
            </div>
            <div>
              <p className="text-foreground-950 font-semibold text-lg">Property Listed Successfully!</p>
              <p className="text-foreground-600 text-sm mt-1">
                Our team will review your submission within 24 hours and get back to you.
              </p>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => navigate("/listings")}
                  className="px-4 py-2 rounded-lg bg-emerald-500 text-background-50 font-medium text-sm hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Browse Listings
                </button>
                <button
                  onClick={() => setStatus("idle")}
                  className="px-4 py-2 rounded-lg border border-foreground-300 text-foreground-700 font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Submit Another
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form + Sidebar */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Form Column */}
            <div className="lg:col-span-2">
              <div className="bg-background-50 border border-primary-900/10 rounded-2xl p-6 md:p-8">
                <h2 className="text-2xl font-bold text-foreground-950 mb-1">{formHeading}</h2>
                <p className="text-foreground-600 text-sm mb-8">
                  {formSub}
                </p>

                <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="contact_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 w-0 h-0"
                  />

                  {/* Property Images */}
                  <div>
                    <p className="text-primary-500 text-xs font-black uppercase tracking-widest mb-4 flex items-center gap-2">
                      <i className="ri-image-2-line" />
                      Property Images
                      <span className="text-foreground-500 font-normal normal-case text-[10px]">(optional)</span>
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {imagePreviews.map((preview, idx) => (
                        <div key={idx} className="relative w-16 h-16 rounded-xl overflow-hidden border border-primary-900/20">
                          <img src={preview} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute top-0.5 right-0.5 w-5 h-5 bg-background-950/70 text-background-50 rounded-full flex items-center justify-center text-[10px] hover:bg-red-500 transition-colors cursor-pointer"
                          >
                            <i className="ri-close-line" />
                          </button>
                        </div>
                      ))}
                      {imagePreviews.length < 5 && (
                        <label className="w-16 h-16 rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors border-foreground-400 hover:border-primary-500/60 bg-background-100 hover:bg-primary-500/5">
                          <i className="ri-image-add-line text-foreground-400 text-lg" />
                          <span className="text-[9px] text-foreground-400 mt-0.5">Add</span>
                          <input
                            ref={fileInputRef}
                            accept="image/*"
                            multiple
                            className="hidden"
                            type="file"
                            onChange={handleImageUpload}
                          />
                        </label>
                      )}
                    </div>
                    <p className="text-foreground-500 text-xs mt-2">First image will be the main display photo. Max 5 images.</p>
                  </div>

                  {/* Quick Enquiry */}
                  <div>
                    <p className="text-primary-500 text-xs font-black uppercase tracking-widest mb-4 flex items-center gap-2">
                      <i className="ri-file-list-2-line" />
                      Quick Enquiry
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-foreground-700 text-xs font-semibold mb-1.5 block">
                          Land Owner Name <span className="text-primary-500">*</span>
                        </label>
                        <input
                          required
                          name="owner_name"
                          placeholder="Your full name"
                          className="w-full bg-background-100 border border-primary-900/20 text-foreground-950 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500/60 placeholder-foreground-400 transition-colors"
                        />
                      </div>

                      <div>
                        <label className="text-foreground-700 text-xs font-semibold mb-1.5 block">
                          Mobile Number <span className="text-primary-500">*</span>
                        </label>
                        <input
                          required
                          name="mobile"
                          type="tel"
                          placeholder="+91 XXXXX XXXXX"
                          className="w-full bg-background-100 border border-primary-900/20 text-foreground-950 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500/60 placeholder-foreground-400 transition-colors"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-foreground-700 text-xs font-semibold mb-1.5 block">
                          Property Title <span className="text-primary-500">*</span>
                        </label>
                        <input
                          required
                          name="title"
                          placeholder="e.g. Prime Agricultural Land - Tumakuru Highway"
                          className="w-full bg-background-100 border border-primary-900/20 text-foreground-950 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500/60 placeholder-foreground-400 transition-colors"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-foreground-700 text-xs font-semibold mb-1.5 block">
                          Property Description
                        </label>
                        <textarea
                          name="description"
                          rows={3}
                          maxLength={500}
                          onChange={(e) => setDescCount(e.target.value.length)}
                          placeholder="Brief description of the property - facilities, previous use, highlights..."
                          className="w-full bg-background-100 border border-primary-900/20 text-foreground-950 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500/60 placeholder-foreground-400 transition-colors resize-none"
                        />
                        <p className="text-foreground-500 text-xs mt-1 text-right">{descCount}/500</p>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-foreground-700 text-xs font-semibold mb-1.5 block">
                          Location Address <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          name="location_address"
                          required
                          rows={2}
                          maxLength={300}
                          placeholder="Full address - area, city, district, state, pincode"
                          className="w-full bg-background-100 border border-primary-900/20 text-foreground-950 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500/60 placeholder-foreground-400 transition-colors resize-none"
                        />
                      </div>

                      <div>
                        <label className="text-foreground-700 text-xs font-semibold mb-1.5 block">
                          City <span className="text-primary-500">*</span>
                        </label>
                        <CityAutocomplete
                          value={city}
                          onChange={setCity}
                          onStateChange={setStateProvince}
                          state={stateProvince || undefined}
                          name="city"
                          placeholder="Type city name (min 2 letters)..."
                        />
                      </div>

                      <div>
                        <label className="text-foreground-700 text-xs font-semibold mb-1.5 block">
                          State / UT <span className="text-primary-500">*</span>
                        </label>
                        <select
                          name="state_province"
                          value={stateProvince}
                          onChange={(e) => setStateProvince(e.target.value)}
                          className="w-full bg-background-100 border border-primary-900/20 text-foreground-950 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500/60 transition-colors cursor-pointer"
                        >
                          <option value="">Select state / UT</option>
                          {INDIAN_STATES.map((state) => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-foreground-700 text-xs font-semibold mb-1.5 block">
                          Google Map Link
                        </label>
                        <input
                          name="google_map_link"
                          type="url"
                          placeholder="Paste Google Maps share link"
                          className="w-full bg-background-100 border border-primary-900/20 text-foreground-950 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500/60 placeholder-foreground-400 transition-colors"
                        />
                      </div>

                      <div>
                        <label className="text-foreground-700 text-xs font-semibold mb-1.5 block">Land Size</label>
                        <input
                          name="land_size"
                          placeholder="e.g. 5 acres, 10 guntha"
                          className="w-full bg-background-100 border border-primary-900/20 text-foreground-950 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500/60 placeholder-foreground-400 transition-colors"
                        />
                      </div>

                      <div>
                        <label className="text-foreground-700 text-xs font-semibold mb-1.5 block">Category</label>
                        <select
                          name="category"
                          className="w-full bg-background-100 border border-primary-900/20 text-foreground-950 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500/60 transition-colors"
                        >
                          {categoryOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-foreground-700 text-xs font-semibold mb-1.5 block">
                          Additional Information
                        </label>
                        <textarea
                          name="additional_info"
                          rows={3}
                          maxLength={500}
                          onChange={(e) => setAddCount(e.target.value.length)}
                          placeholder="Any other details - restrictions, previous use, nearby landmarks, special features..."
                          className="w-full bg-background-100 border border-primary-900/20 text-foreground-950 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500/60 placeholder-foreground-400 transition-colors resize-none"
                        />
                        <p className="text-foreground-500 text-xs mt-1 text-right">{addCount}/500</p>
                      </div>

                      {/* Available for */}
                      <div className="sm:col-span-2">
                        <label className="text-foreground-700 text-xs font-semibold mb-1.5 block">
                          Available for
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {transactionOptions.map((opt) => (
                            <label
                              key={opt.value}
                              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors select-none text-sm ${
                                selectedTransactionTypes.includes(opt.value)
                                  ? "bg-primary-500/10 border-primary-500/40 text-primary-700 font-semibold"
                                  : "bg-background-100 border-primary-900/20 text-foreground-600 hover:border-primary-500/40"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="w-3.5 h-3.5 accent-primary-500"
                                checked={selectedTransactionTypes.includes(opt.value)}
                                onChange={() => toggleTransactionType(opt.value)}
                              />
                              {opt.label}
                            </label>
                          ))}
                        </div>
                        <p className="text-foreground-500 text-xs mt-1.5">
                          Select how you want to offer this property — you can pick more than one.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Error */}
                  {status === "error" && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
                      <i className="ri-error-warning-line text-red-500 text-lg shrink-0 mt-0.5" />
                      <p className="text-red-600 text-sm">{errorMsg}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-background-50 font-bold py-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-base"
                  >
                    <i className={`${loading ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} text-lg`} />
                    <span>{loading ? "Submitting..." : "Submit My Land Listing"}</span>
                  </button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Why List With Us */}
              <div className="bg-gradient-to-br from-primary-500/20 to-primary-600/10 border border-primary-500/30 rounded-2xl p-7">
                <h3 className="text-foreground-950 font-bold text-lg mb-5">{whyHeading}</h3>
                <ul className="space-y-4">
                  {whyListItems.map((item) => (
                    <li key={item.label} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-primary-500/10 rounded-lg shrink-0">
                        <i className={`${item.icon} text-primary-500 text-base`} />
                      </div>
                      <span className="text-foreground-700 text-sm leading-snug">{item.label}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Need Help */}
              <div className="bg-background-50 border border-primary-900/10 rounded-2xl p-6">
                <h3 className="text-foreground-950 font-bold text-base mb-3">Need Help?</h3>
                <p className="text-foreground-600 text-sm mb-4">
                  Talk to our team directly on WhatsApp for instant assistance.
                </p>
                <a
                  href={buildWhatsAppLink(settings.general.whatsapp_number)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-background-50 font-semibold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg" />
                  <span>Chat: {settings.general.whatsapp_number || "+91 90350 63455"}</span>
                </a>
              </div>

              {/* What Happens Next */}
              <div className="bg-background-50 border border-primary-900/10 rounded-2xl p-6">
                <h3 className="text-foreground-950 font-bold text-base mb-4">{stepsHeading}</h3>
                <div className="space-y-4">
                  {steps.map((step) => (
                    <div key={step.num} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-primary-500/20 border border-primary-500/30 flex items-center justify-center shrink-0">
                        <span className="text-primary-500 text-xs font-black">{step.num}</span>
                      </div>
                      <div>
                        <p className="text-foreground-950 text-sm font-semibold">{step.title}</p>
                        <p className="text-foreground-600 text-xs mt-0.5">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Testimonial */}
              <div className="bg-background-50 border border-primary-900/10 rounded-2xl overflow-hidden">
                <img
                  alt="Happy landowner"
                  className="w-full h-40 object-cover object-top"
                  src="https://readdy.ai/api/search-image?query=happy%20Indian%20landowner%20shaking%20hands%20with%20real%20estate%20agent%20outdoors%20near%20open%20land%20plot%2C%20professional%20business%20agreement%2C%20warm%20golden%20light%2C%20trust%20and%20satisfaction%20expression&width=400&height=240&seq=submit-testi-001&orientation=landscape"
                />
                <div className="p-5">
                  <p className="text-foreground-700 text-sm italic leading-relaxed">
                    "Listed my 3-acre land and had 4 enquiries in the first week. Deal closed in 18 days!"
                  </p>
                  <p className="text-primary-500 text-xs font-semibold mt-3">- Rajesh P., Bengaluru</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}